<template>
  <div>
    <button @click="$emit('exit')" class="text-sm text-gray-500 hover:text-gray-700 mb-4">&larr; All games</button>

    <div v-if="loading" class="text-center py-16 text-gray-500">Loading tone drills…</div>
    <div v-else-if="error" class="text-center py-16 text-red-500">{{ error }}</div>

    <!-- Results -->
    <div v-else-if="finished" class="bg-white rounded-3xl border border-gray-200 shadow-sm p-8 text-center">
      <div class="text-4xl mb-2">🎵</div>
      <h2 class="text-2xl font-bold text-gray-900">{{ correct }} / {{ questions.length }} correct</h2>
      <p class="mt-1 text-gray-600">Best combo ×{{ maxCombo }} · Score <span class="font-bold text-emerald-600">{{ score }}</span>
        <span v-if="bestScore"> · Best {{ bestScore }}</span></p>
      <p class="mt-1 text-emerald-600 font-medium">+{{ xpEarned }} XP<span v-if="xpEarned === 0" class="text-gray-400"> (daily cap reached)</span></p>
      <div class="mt-6 flex gap-3 justify-center">
        <button @click="restart" class="bg-emerald-600 text-white font-medium px-6 py-3 rounded-xl hover:bg-emerald-700">Play again</button>
        <button @click="$emit('exit')" class="bg-gray-100 text-gray-700 font-medium px-6 py-3 rounded-xl hover:bg-gray-200">Done</button>
      </div>
    </div>

    <!-- Playing -->
    <div v-else-if="current">
      <div class="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8 text-center">
        <div class="flex items-center justify-between text-xs font-medium text-gray-400">
          <span>🎵 Tone Match</span>
          <span>{{ index + 1 }} / {{ questions.length }}</span>
        </div>
        <div class="mt-2 h-1.5 w-full rounded-full bg-gray-100">
          <div class="h-1.5 rounded-full bg-emerald-500 transition-all duration-300"
               :style="{ width: `${(index / questions.length) * 100}%` }"></div>
        </div>

        <div v-if="combo >= 2" class="mt-4">
          <span class="inline-block bg-amber-100 text-amber-700 font-bold px-3 py-1 rounded-full text-sm">🔥 Combo ×{{ combo }}</span>
        </div>

        <p class="text-sm text-gray-500 mt-5 mb-4">Listen, then pick the word you heard.</p>
        <div class="flex justify-center mb-6">
          <AudioButton :key="index" :src="current.audio_url" :text="current.audio_text" :autoplay="true" class="scale-150" />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <button
            v-for="(opt, i) in current.options" :key="i"
            @click="!answered && choose(opt)"
            :disabled="answered"
            class="relative px-4 py-4 rounded-xl border-2 text-2xl font-semibold transition-all"
            :class="optionClass(opt)"
          >
            {{ opt.text }}
            <span v-if="answered && opt.is_correct" class="absolute top-2 right-2.5 text-base text-emerald-600">&#10003;</span>
            <span v-else-if="answered && opt === selected" class="absolute top-2 right-2.5 text-base text-red-500">&#10007;</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { apiGet, apiPost } from '../../api/client'
import AudioButton from '../shared/AudioButton.vue'

defineEmits(['exit'])

const loading = ref(true)
const error = ref(null)
const questions = ref([])
const index = ref(0)
const answered = ref(false)
const selected = ref(null)
const combo = ref(0)
const maxCombo = ref(0)
const correct = ref(0)
const score = ref(0)
const finished = ref(false)
const xpEarned = ref(0)
const bestScore = ref(null)

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
const current = computed(() => questions.value[index.value] || null)

function optionClass(opt) {
  if (!answered.value) return 'border-gray-200 bg-white text-gray-800 hover:border-emerald-400'
  if (opt.is_correct) return 'border-emerald-500 bg-emerald-50 text-emerald-700'
  if (opt === selected.value) return 'border-red-500 bg-red-50 text-red-700'
  return 'border-gray-200 bg-white text-gray-400'
}

function choose(opt) {
  selected.value = opt
  answered.value = true
  if (opt.is_correct) {
    combo.value += 1
    maxCombo.value = Math.max(maxCombo.value, combo.value)
    correct.value += 1
    score.value += 100 * combo.value
  } else {
    combo.value = 0
  }
  setTimeout(next, 900)
}

function next() {
  if (index.value + 1 < questions.value.length) {
    index.value += 1
    answered.value = false
    selected.value = null
  } else {
    finish()
  }
}

async function finish() {
  finished.value = true
  try {
    const res = await apiPost('/api/v1/games/record', {
      game: 'tone_trainer', score: score.value, correct: correct.value,
      total: questions.value.length, timezone: tz,
    })
    xpEarned.value = res?.xp_earned ?? 0
    bestScore.value = res?.best_score ?? null
  } catch (e) { /* results still shown */ }
}

async function load() {
  loading.value = true
  error.value = null
  finished.value = false
  index.value = 0
  combo.value = 0
  maxCombo.value = 0
  correct.value = 0
  score.value = 0
  answered.value = false
  selected.value = null
  try {
    const data = await apiGet('/api/v1/games/tone_drills?count=10')
    questions.value = (data.exercises || []).filter(e => e.options && e.options.length)
    if (questions.value.length === 0) error.value = 'Practice a few lessons first to unlock Tone Match!'
  } catch (e) {
    error.value = 'Could not load Tone Match.'
  } finally {
    loading.value = false
  }
}

function restart() { load() }

onMounted(load)
</script>
